/**
 * Shared prop transform math (gameplay + editor).
 * Offsets are local to the ground anchor; worldAspect = width/height.
 */

import type { SpawnPoint } from '@/world-prototype/types';

/** Normalize degrees into (−180, 180]. */
export function normalizeRotationDeg(degrees: number): number {
  let d = degrees % 360;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  if (d === -180) return 180;
  return d;
}

export function snapRotationDeg(degrees: number, snap: number): number {
  if (snap <= 0) return normalizeRotationDeg(degrees);
  return normalizeRotationDeg(Math.round(degrees / snap) * snap);
}

/**
 * Rotate a local offset around the ground anchor.
 * Matches CSS `rotate()` in a Y-down layer (positive = clockwise on screen).
 */
export function rotateOffset(
  offset: SpawnPoint,
  rotationDeg: number,
  worldAspect: number,
): SpawnPoint {
  if (!rotationDeg) return { x: offset.x, y: offset.y };
  const aspect = worldAspect > 0 ? worldAspect : 1;
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: offset.x * cos - (offset.y / aspect) * sin,
    y: offset.x * aspect * sin + offset.y * cos,
  };
}

export function rotateOffsets(
  offsets: readonly SpawnPoint[],
  rotationDeg: number,
  worldAspect: number,
): SpawnPoint[] {
  return offsets.map((p) => rotateOffset(p, rotationDeg, worldAspect));
}

/** Mirror a local offset across the upright axis through the ground anchor. */
export function flipOffsetX(offset: SpawnPoint, flipX: boolean): SpawnPoint {
  return flipX ? { x: -offset.x, y: offset.y } : { x: offset.x, y: offset.y };
}

export function flipOffsetsX(
  offsets: readonly SpawnPoint[],
  flipX: boolean,
): SpawnPoint[] {
  return flipX ? offsets.map((p) => ({ x: -p.x, y: p.y })) : offsets.map((p) => ({ ...p }));
}

/**
 * Local authored offset → world offset: optional horizontal flip, then rotate.
 * Matches CSS `rotate() scaleX(-1)` with the layer's transform-origin anchor.
 */
export function localOffsetToWorld(
  offset: SpawnPoint,
  rotationDeg: number,
  worldAspect: number,
  flipX = false,
): SpawnPoint {
  return rotateOffset(flipOffsetX(offset, flipX), rotationDeg, worldAspect);
}

export function localOffsetsToWorld(
  offsets: readonly SpawnPoint[],
  rotationDeg: number,
  worldAspect: number,
  flipX = false,
): SpawnPoint[] {
  return rotateOffsets(flipOffsetsX(offsets, flipX), rotationDeg, worldAspect);
}

/** World offset → authored local: un-rotate, then un-flip. */
export function worldOffsetToLocal(
  offset: SpawnPoint,
  rotationDeg: number,
  worldAspect: number,
  flipX = false,
): SpawnPoint {
  return flipOffsetX(
    rotateOffset(offset, -rotationDeg, worldAspect),
    flipX,
  );
}

export function worldOffsetsToLocal(
  offsets: readonly SpawnPoint[],
  rotationDeg: number,
  worldAspect: number,
  flipX = false,
): SpawnPoint[] {
  return flipOffsetsX(
    rotateOffsets(offsets, -rotationDeg, worldAspect),
    flipX,
  );
}
